import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Divider,
  Badge,
  CircularProgress,
  Container,
  Tooltip,
  useMediaQuery,
  useTheme,
  Button,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
  limit,
  increment,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { format, formatDistanceToNow, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

const Messages = () => {
  const { currentUser } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const { markAsRead } = useNotifications();
  const [error, setError] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef(null);

  // Default user ID since authentication is not implemented yet
  const defaultUserId = currentUser?.uid || 'default-user-id';

  // Get conversation ID from query parameter if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const conversationId = params.get('conversation');
    
    if (conversationId) {
      try {
        // Simple approach - just set the ID and other details will be loaded by the query
        getDoc(doc(db, 'conversations', conversationId))
          .then(docSnapshot => {
            if (docSnapshot.exists()) {
              setActiveConversation({
                ...docSnapshot.data(),
                id: conversationId
              });
            } else {
              console.error('Conversation not found');
              setError('No se encontró la conversación');
            }
          })
          .catch(error => {
            console.error('Error fetching conversation:', error);
            setError('Error al cargar la conversación');
          });
      } catch (error) {
        console.error('Error in conversation parameter handling:', error);
        setError('Error al procesar los parámetros de conversación');
      }
    }
  }, [location]);

  // Load user's conversations - with better error handling
  useEffect(() => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use the current user ID from auth
      const userId = currentUser.uid;
      
      // Simpler approach to avoid Firestore internal errors
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('userId', '==', userId)
      );
      
      const unsubscribe = onSnapshot(
        conversationsQuery, 
        (snapshot) => {
          const conversationList = [];
          snapshot.forEach((doc) => {
            conversationList.push({
              ...doc.data(),
              id: doc.id
            });
          });
          
          setConversations(conversationList);
          setFilteredConversations(conversationList);
          setLoading(false);
        },
        (error) => {
          console.error('Error listening to conversations:', error);
          setError('Error al cargar las conversaciones');
          setLoading(false);
        }
      );
      
      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up conversations listener:', error);
      setError('Error al cargar las conversaciones');
      setLoading(false);
    }
  }, [currentUser]);

  // Filter conversations when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredConversations(conversations);
      return;
    }
    
    const filtered = conversations.filter(
      (conversation) =>
        conversation.providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conversation.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredConversations(filtered);
  }, [searchTerm, conversations]);

  // Separate function to fetch messages to improve error handling
  const fetchMessages = useCallback((conversationId) => {
    if (!conversationId) return () => {}; // Return a no-op function
    
    try {
      const messagesQuery = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId)
      );
      
      const unsubscribe = onSnapshot(
        messagesQuery,
        async (snapshot) => {
          const messageList = [];
          snapshot.forEach((doc) => {
            messageList.push({
              id: doc.id,
              ...doc.data(),
              timestamp: doc.data().timestamp?.toDate() || new Date()
            });
          });
          
          // Sort messages by timestamp (as orderBy might cause issues)
          messageList.sort((a, b) => a.timestamp - b.timestamp);
          
          setMessages(messageList);
          setLoadingMessages(false);
          
          // Mark conversation as read
          if (activeConversation?.id && activeConversation.userUnreadCount > 0) {
            try {
              await updateDoc(doc(db, 'conversations', activeConversation.id), {
                userUnreadCount: 0
              });
              
              // Mark associated notification as read if the function exists
              if (typeof markAsRead === 'function') {
                markAsRead(activeConversation.id);
              }
            } catch (error) {
              console.error('Error updating read status:', error);
            }
          }
        },
        (error) => {
          console.error('Error listening to messages:', error);
          setError('Error al cargar los mensajes');
          setLoadingMessages(false);
        }
      );
      
      // Return the actual unsubscribe function
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up messages listener:', error);
      setError('Error al cargar los mensajes');
      setLoadingMessages(false);
      return () => {}; // Return a no-op function on error
    }
  }, [activeConversation, markAsRead]);

  // Load messages for active conversation - improved
  useEffect(() => {
    if (!activeConversation || !activeConversation.id) {
      setMessages([]);
      return;
    }
    
    setLoadingMessages(true);
    setError(null);
    
    // Set up messages listener directly
    const unsubscribe = fetchMessages(activeConversation.id);
    
    // Cleanup function - make sure unsubscribe is a function
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [activeConversation, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Send a new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation || !currentUser) return;
    
    try {
      // Add message to Firestore using the authenticated user ID
      await addDoc(collection(db, 'messages'), {
        conversationId: activeConversation.id,
        content: newMessage.trim(),
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Usuario',
        timestamp: serverTimestamp(),
        type: 'text'
      });
      
      // Update conversation with last message
      await updateDoc(doc(db, 'conversations', activeConversation.id), {
        lastMessage: newMessage.trim(),
        lastMessageTimestamp: serverTimestamp(),
        // Increment unread count for the provider
        providerUnreadCount: activeConversation.providerUnreadCount + 1
      });
      
      // Clear input
      setNewMessage('');
      
      // Scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Error al enviar el mensaje');
    }
  };

  // Format messages for display
  const formatMessages = () => {
    let formattedMessages = [];
    let currentDate = null;
    
    messages.forEach((message, index) => {
      const messageDate = new Date(message.timestamp);
      
      // Add date separator if this is a new day
      if (!currentDate || !isSameDay(currentDate, messageDate)) {
        currentDate = messageDate;
        formattedMessages.push({
          id: `date-${message.id}`,
          type: 'date',
          date: messageDate
        });
      }
      
      // Add the actual message
      formattedMessages.push({
        ...message,
        type: 'message'
      });
    });
    
    return formattedMessages;
  };

  // Format date for display
  const formatMessageDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (isSameDay(date, today)) {
      return 'Hoy';
    } else if (isSameDay(date, yesterday)) {
      return 'Ayer';
    } else {
      return format(date, 'EEEE, d MMMM', { locale: es });
    }
  };

  // Handle selecting a conversation
  const handleConversationSelect = (conversation) => {
    // Make sure the conversation has an ID
    if (!conversation || !conversation.id) {
      console.error('Invalid conversation selected');
      return;
    }
    
    setActiveConversation(conversation);
    
    // Update URL with conversation ID
    navigate(`/messages?conversation=${conversation.id}`);
    
    // On mobile, hide the conversations list
    if (isMobile) {
      // You could set state to hide the conversation list here
    }
  };

  // Back button handler for mobile
  const handleBackToList = () => {
    setActiveConversation(null);
    navigate('/messages');
  };

  // Handle booking related to the conversation
  const handleViewBookings = () => {
    navigate('/bookings');
  };

  // Format a message for display - updated to use currentUser
  const formatMessageContent = (message) => {
    // Check if it's a booking-related message
    if (message.type === 'booking-request') {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Solicitud de Reserva
          </Typography>
          <Typography variant="body2">{message.content}</Typography>
          <Button 
            size="small" 
            startIcon={<EventIcon />}
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => navigate('/bookings')}
          >
            Ver Reservas
          </Button>
        </Box>
      );
    } else if (message.type === 'booking-response') {
      return (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 1, 
              color: message.bookingStatus === 'accepted' ? 'success.main' : 'error.main'
            }}
          >
            {message.bookingStatus === 'accepted' 
              ? 'Reserva Aceptada' 
              : 'Reserva Rechazada'}
          </Typography>
          <Typography variant="body2">{message.content}</Typography>
          <Button 
            size="small" 
            startIcon={<EventIcon />}
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => navigate('/bookings')}
          >
            Ver Reservas
          </Button>
        </Box>
      );
    }
    
    // Regular text message
    return <Typography variant="body2">{message.content}</Typography>;
  };

  // In the messages display section, update the isUser check
  const isUser = (senderId) => senderId === currentUser?.uid;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#2C3E50' }}>
        Mis Mensajes
      </Typography>
      
      {!currentUser ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Inicia sesión para acceder a tus mensajes.
        </Alert>
      ) : null}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Conversations List */}
        <Grid 
          item 
          xs={12} 
          md={4} 
          sx={{ 
            display: isMobile && activeConversation ? 'none' : 'block' 
          }}
        >
          <Paper sx={{ borderRadius: '12px', overflow: 'hidden', height: '70vh' }}>
            {/* Search */}
            <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
              <TextField
                fullWidth
                placeholder="Buscar conversaciones..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#999', mr: 1 }} />,
                  endAdornment: searchTerm && (
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                  sx: { 
                    borderRadius: '30px',
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    },
                    '& fieldset': {
                      border: 'none'
                    }
                  }
                }}
                size="small"
              />
            </Box>
            
            {/* Conversations */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredConversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  {searchTerm ? 'No se encontraron conversaciones que coincidan con tu búsqueda' : 'No tienes conversaciones aún'}
                </Typography>
              </Box>
            ) : (
              <List disablePadding sx={{ overflow: 'auto', maxHeight: 'calc(70vh - 60px)' }}>
                {filteredConversations.map((conversation) => (
                  <React.Fragment key={conversation.id}>
                    <ListItem
                      button
                      selected={activeConversation?.id === conversation.id}
                      onClick={() => handleConversationSelect({
                        ...conversation,
                        id: conversation.id  // Ensure ID is included
                      })}
                      sx={{
                        px: 2,
                        py: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 213, 0, 0.1)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          color="error"
                          badgeContent={conversation.userUnreadCount}
                          overlap="circular"
                          invisible={!conversation.userUnreadCount}
                        >
                          <Avatar 
                            alt={conversation.providerName} 
                            src={conversation.providerImage || undefined}
                            sx={{ bgcolor: '#FFD700', color: '#333' }}
                          >
                            {conversation.providerName?.charAt(0) || 'P'}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: conversation.userUnreadCount ? 600 : 400,
                              color: '#2C3E50'
                            }}
                          >
                            {conversation.providerName}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: conversation.userUnreadCount ? '#000' : '#666',
                                fontWeight: conversation.userUnreadCount ? 500 : 400,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.8125rem'
                              }}
                            >
                              {conversation.lastMessage}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#999',
                                fontSize: '0.75rem'
                              }}
                            >
                              {conversation.lastMessageTimestamp?.toDate 
                                ? formatDistanceToNow(conversation.lastMessageTimestamp.toDate(), { addSuffix: true, locale: es })
                                : ''}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{
                          noWrap: true,
                          style: { marginBottom: 4 }
                        }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
        
        {/* Messages Area */}
        <Grid 
          item 
          xs={12} 
          md={8}
          sx={{ 
            display: !isMobile || activeConversation ? 'block' : 'none'
          }}
        >
          {activeConversation ? (
            <Paper sx={{ borderRadius: '12px', height: '70vh', display: 'flex', flexDirection: 'column' }}>
              {/* Chat Header */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderBottom: '1px solid #eee', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isMobile && (
                    <IconButton edge="start" color="inherit" onClick={handleBackToList} sx={{ mr: 1 }}>
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Avatar 
                    alt={activeConversation.providerName} 
                    src={activeConversation.providerImage || undefined}
                    sx={{ bgcolor: '#FFD700', color: '#333', width: 40, height: 40 }}
                  >
                    {activeConversation.providerName?.charAt(0) || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                      {activeConversation.providerName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Proveedor
                    </Typography>
                  </Box>
                </Box>
                
                {/* Actions */}
                <Box>
                  <Tooltip title="Ver Reservaciones">
                    <IconButton color="primary" onClick={handleViewBookings}>
                      <CalendarIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              {/* Messages */}
              <Box 
                sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto', 
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  backgroundColor: '#f9f9f9'
                }}
              >
                {loadingMessages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    textAlign: 'center',
                    gap: 2,
                    p: 3
                  }}>
                    <Typography variant="h6" color="textSecondary">
                      Sin mensajes aún
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Envía un mensaje para iniciar una conversación con {activeConversation.providerName}
                    </Typography>
                  </Box>
                ) : (
                  formatMessages().map((item) => {
                    if (item.type === 'date') {
                      return (
                        <Box 
                          key={item.id} 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            my: 1.5 
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                              color: '#666',
                              py: 0.5,
                              px: 2,
                              borderRadius: '20px',
                              fontWeight: 500
                            }}
                          >
                            {formatMessageDate(item.date)}
                          </Typography>
                        </Box>
                      );
                    } else if (item.type === 'message') {
                      // Use default user ID instead of auth.currentUser to determine if message is from user
                      const isUser = item.senderId === currentUser?.uid;
                      const isBookingRequest = item.type === 'booking';
                      const isBookingResponse = item.type === 'booking-response';
                      
                      // Determine if this is a booking-related message
                      const isBookingRelated = isBookingRequest || isBookingResponse;
                      
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            alignSelf: isUser ? 'flex-end' : 'flex-start',
                            maxWidth: isBookingRelated ? '80%' : '70%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            mb: isBookingRelated ? 2 : 1
                          }}
                        >
                          {isBookingRequest && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: '12px',
                                backgroundColor: '#FFF9C4',
                                borderLeft: '3px solid #FFD700'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <EventIcon sx={{ color: '#FFD700' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Solicitud de Reserva
                                </Typography>
                              </Box>
                              <Typography variant="body2">{item.content}</Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  {format(item.timestamp, 'HH:mm')}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleViewBookings}
                                  sx={{
                                    minWidth: 0,
                                    borderColor: '#FFD700',
                                    color: '#333',
                                    fontSize: '0.75rem',
                                    p: '2px 8px',
                                    borderRadius: '12px'
                                  }}
                                >
                                  Ver reservas
                                </Button>
                              </Box>
                            </Paper>
                          )} 
                          
                          {isBookingResponse && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: '12px',
                                backgroundColor: item.bookingStatus === 'accepted' ? '#E8F5E9' : '#FFEBEE',
                                borderLeft: item.bookingStatus === 'accepted' ? '3px solid #4CAF50' : '3px solid #F44336'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {item.bookingStatus === 'accepted' ? (
                                  <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                                ) : (
                                  <CancelIcon sx={{ color: '#F44336' }} />
                                )}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {item.bookingStatus === 'accepted' ? 'Reserva Aceptada' : 'Reserva Cancelada'}
                                </Typography>
                              </Box>
                              <Typography variant="body2">{item.content}</Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  {format(item.timestamp, 'HH:mm')}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleViewBookings}
                                  sx={{
                                    minWidth: 0,
                                    borderColor: item.bookingStatus === 'accepted' ? '#4CAF50' : '#F44336',
                                    color: item.bookingStatus === 'accepted' ? '#4CAF50' : '#F44336',
                                    fontSize: '0.75rem',
                                    p: '2px 8px',
                                    borderRadius: '12px'
                                  }}
                                >
                                  Ver reservas
                                </Button>
                              </Box>
                            </Paper>
                          )}
                          
                          {!isBookingRelated && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: isUser 
                                  ? '12px 12px 0 12px' 
                                  : '12px 12px 12px 0',
                                backgroundColor: isUser ? '#FFE500' : '#fff',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                              }}
                            >
                              <Typography variant="body1" sx={{ color: isUser ? '#333' : '#333' }}>
                                {formatMessageContent(item)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                align="right" 
                                sx={{ 
                                  display: 'block', 
                                  mt: 0.5, 
                                  color: isUser ? 'rgba(0,0,0,0.5)' : '#999',
                                  textAlign: 'right'
                                }}
                              >
                                {format(item.timestamp, 'HH:mm')}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      );
                    }
                    
                    return null;
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>
              
              {/* Message Input */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderTop: '1px solid #eee',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Escribe un mensaje..."
                  variant="outlined"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  InputProps={{
                    sx: { 
                      borderRadius: '30px',
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#f0f0f0'
                      }
                    }
                  }}
                  multiline
                  maxRows={3}
                />
                <IconButton 
                  color="primary" 
                  onClick={handleSendMessage}
                  sx={{ 
                    backgroundColor: '#FFD700',
                    color: '#333',
                    '&:hover': {
                      backgroundColor: '#FFE500'
                    }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Paper>
          ) : (
            <Paper 
              sx={{ 
                borderRadius: '12px', 
                height: '70vh', 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center'
              }}
            >
              <Box sx={{ m: 'auto', maxWidth: 400 }}>
                <Box 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3 
                  }}
                >
                  <MessageIcon sx={{ fontSize: 40, color: '#FFD700' }} />
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, color: '#2C3E50' }}>
                  Tus Mensajes
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                  Selecciona una conversación de la lista para comenzar a chatear o para ver mensajes anteriores
                </Typography>
                {!isMobile && filteredConversations.length === 0 && (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/')}
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#333',
                      '&:hover': {
                        backgroundColor: '#FFE500'
                      },
                      borderRadius: '8px',
                      textTransform: 'none',
                      py: 1
                    }}
                  >
                    Buscar Proveedores
                  </Button>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Messages; 